// useDeskReadiness — the export readiness (Studio desk decision 3) of each
// module or course open on the desk, for its foot. Shell-side: it asks the
// server whenever what is open or the library changes (a document planned,
// written or taken out moves the line), and hands the answers to the desk.
// A failed ask leaves that container without a line.

export { useDeskReadiness };

import { Result } from "@bilda/utils";
import { useEffect, useState } from "react";
import type { DeskState } from "../../../components/Studio/studioDesk.js";
import type { ExportReadiness } from "../../ports/brief.js";
import type { AppPorts, Library } from "../../ports.js";

const useDeskReadiness = (
    ports: AppPorts,
    desk: DeskState,
    library: Library | null,
): ReadonlyMap<string, ExportReadiness> => {
    const [readiness, setReadiness] = useState<
        ReadonlyMap<string, ExportReadiness>
    >(new Map());
    const open = desk.path
        .filter((e) => e.kind !== "document")
        .map((e) => e.id)
        .join(" ");
    useEffect(() => {
        if (library === null) return;
        const ids = open === "" ? [] : open.split(" ");
        let current = true;
        const go = async () => {
            const answers = await Promise.all(
                ids.map(async (id) => ({
                    id,
                    r: await ports.loadExportReadiness(id),
                })),
            );
            if (!current) return;
            setReadiness(
                new Map(
                    answers.flatMap(
                        ({ id, r }): readonly [string, ExportReadiness][] =>
                            Result.isOk(r) ? [[id, r.value]] : [],
                    ),
                ),
            );
        };
        void go();
        return () => {
            current = false;
        };
    }, [ports, open, library]);
    return readiness;
};
